// Re-export generated types from the GraphQL SDK — do not duplicate here.
export type {
	AgentHealth,
	AgentKind,
	AgentRole,
	AgentStatus,
	AuditAction,
	ProjectStatus,
} from "./artemis-sdk";

import type {
	CreateAgentMutation,
	GetAgentQuery,
	GetApprovalQuery,
	GetAuditLogsQuery,
	GetEnvironmentQuery,
	GetGitProviderConnectionQuery,
	GetGitProviderConnectionRepositoriesQuery,
	GetGitProviderRepositoryBranchesQuery,
	GetGitProviderRepositoryCommitsQuery,
	GetJobQuery,
	GetJobRunQuery,
	GetOrganizationAppsQuery,
	GetOrganizationQuery,
	GetProjectCatalogGraphQuery,
	GetProjectQuery,
	GetProjectRepositoriesQuery,
	GetReportQuery,
	GetReportsQuery,
	GetSignalCandidatesQuery,
	GetSignalSourcesQuery,
	GetSignalStatusesQuery,
	GetSignalsQuery,
	GetTaskQuery,
	GetTaskTemplateQuery,
	GetUserQuery,
	GetUsersQuery,
	GetWebhookDeliveriesQuery,
	GetWebhookQuery,
	GetWebhooksQuery,
	GetWorkspaceAppsQuery,
	GetWorkspaceMembersQuery,
	GetWorkspaceQuery,
} from "@/common/artemis-sdk/graphql";

// For the Task shape, derive it from the query result:
export type ArtemisUser = NonNullable<GetUserQuery["user"]>;
export type ArtemisTask = NonNullable<GetTaskQuery["task"]>;
export type TaskTemplate = NonNullable<GetTaskTemplateQuery["taskTemplate"]>;
export type Project = NonNullable<GetProjectQuery>["project"];
export type Asset = NonNullable<ArtemisTask>["assets"][number];
export type Workspace = NonNullable<GetWorkspaceQuery>["workspace"];
export type Environment = NonNullable<GetEnvironmentQuery>["environment"];
export type Deployment = NonNullable<Project>["deployments"][number];
export type Agent = NonNullable<GetAgentQuery>["agent"];
// An agent with no A2A endpoint can never be reached or tracked once dispatched (mirrors the
// backend guard in DispatchTaskAsync) — shared by the task detail page and the create wizard so
// both gate "start now" on the same rule.
export function agentIsDispatchable(agent: Agent | undefined): boolean {
	return !!agent?.endpointUrl;
}
// The one-time plaintext credential reveal for External agents (ADR-050) — returned only by
// createAgent / rotateAgentCredential and never retrievable again.
export type AgentCredentialReveal = NonNullable<
	CreateAgentMutation["createAgent"]["credentials"]
>;
export type Organization = NonNullable<GetOrganizationQuery>["organization"];
export type ResourceLink = NonNullable<Project>["links"][number];
// A project's reports are selected with the same fields as ArtemisReport — reuse that type.
export type ProjectReport = ArtemisReport;
export type WorklogEntry = NonNullable<ArtemisTask>["worklog"][number];
export type Worklog = NonNullable<ArtemisTask>["worklog"];
export type Approval = NonNullable<GetApprovalQuery>["approval"];
export type AuditLog = NonNullable<
	NonNullable<GetAuditLogsQuery["auditLogs"]>["nodes"]
>[number];

// TaskStatus as const object for value-level access (e.g. TaskStatus.CREATED).
// Values mirror the GraphQL schema enum exactly — source of truth is the SDK.
export const TaskStatus = {
	APPROVAL_REQUIRED: "APPROVAL_REQUIRED",
	CANCELLED: "CANCELLED",
	COMPLETED: "COMPLETED",
	CREATED: "CREATED",
	FAILED: "FAILED",
	INPUT_REQUIRED: "INPUT_REQUIRED",
	SUBMITTED: "SUBMITTED",
	UNKNOWN: "UNKNOWN",
	WAITING: "WAITING",
	WORKING: "WORKING",
} as const;

export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

/** Final statuses — the server rejects further updates to tasks in these. */
export const TERMINAL_TASK_STATUSES: readonly TaskStatus[] = [
	TaskStatus.COMPLETED,
	TaskStatus.FAILED,
	TaskStatus.CANCELLED,
];

export const AssigneeType = {
	AGENT: "AGENT",
	USER: "USER",
} as const;
export type AssigneeType = (typeof AssigneeType)[keyof typeof AssigneeType];

export type EnvironmentStatus =
	(typeof EnvironmentStatus)[keyof typeof EnvironmentStatus];

export const EnvironmentStatus = {
	ACTIVE: "ACTIVE",
	DISABLED: "DISABLED",
	FAILED: "FAILED",
} as const;

export type EnvironmentClassification =
	(typeof EnvironmentClassification)[keyof typeof EnvironmentClassification];
export const EnvironmentClassification = {
	PRODUCTION: "PRODUCTION",
	NON_PRODUCTION: "NON_PRODUCTION",
} as const;

export type LinkType = (typeof LinkType)[keyof typeof LinkType];
export const LinkType = {
	GENERIC: "GENERIC",
	SOURCE_CODE: "SOURCE_CODE",
	DOCUMENTATION: "DOCUMENTATION",
	TOOL: "TOOL",
} as const;

export type AutoApprovalThreshold =
	(typeof AutoApprovalThreshold)[keyof typeof AutoApprovalThreshold];
export const AutoApprovalThreshold = {
	NONE: "NONE",
	MEDIUM: "MEDIUM",
	HIGH: "HIGH",
} as const;

export type EnvironmentProvider =
	(typeof EnvironmentProvider)[keyof typeof EnvironmentProvider];
export const EnvironmentProvider = {
	AZURE: "AZURE",
	AWS: "AWS",
	CLOUDFLARE: "CLOUDFLARE",
	GOOGLE_CLOUD: "GOOGLE_CLOUD",
	OTHER: "OTHER",
	VERCEL: "VERCEL",
} as const;

export type ProjectType = (typeof ProjectType)[keyof typeof ProjectType];
export const ProjectType = {
	SOFTWARE_APPLICATION: "SOFTWARE_APPLICATION",
	OTHER: "OTHER",
} as const;

export type AssetCategory = (typeof AssetCategory)[keyof typeof AssetCategory];
export const AssetCategory = {
	DOCUMENT: "DOCUMENT",
	BINARY: "BINARY",
	GENERIC: "GENERIC",
	TRANSCRIPT: "TRANSCRIPT",
} as const;

export type ParentType = (typeof ParentType)[keyof typeof ParentType];
export const ParentType = {
	TASK: "TASK",
	PROJECT: "PROJECT",
	REPORT: "REPORT",
} as const;

export type ArtemisReport = NonNullable<
	NonNullable<GetReportsQuery["reports"]>["nodes"]
>[number];

export type ArtemisReportDetail = NonNullable<GetReportQuery["report"]>;

export type ReportType = (typeof ReportType)[keyof typeof ReportType];
export const ReportType = {
	INCIDENT: "INCIDENT",
	INVESTIGATION: "INVESTIGATION",
	GENERIC: "GENERIC",
} as const;

export type ReportStatus = (typeof ReportStatus)[keyof typeof ReportStatus];
export const ReportStatus = {
	OPEN: "OPEN",
	SEALED: "SEALED",
} as const;

export const ApprovalStatus = {
	APPROVED: "APPROVED",
	CONSUMED: "CONSUMED",
	EXPIRED: "EXPIRED",
	INVALIDATED: "INVALIDATED",
	PENDING: "PENDING",
	REJECTED: "REJECTED",
} as const;

export type ApprovalStatus =
	(typeof ApprovalStatus)[keyof typeof ApprovalStatus];

/**
 * Whether an approval can still be approved or rejected.
 *
 * Status alone is not enough: expiry is lazy server-side (there is no sweep), so a request whose
 * approval window has elapsed still reads as PENDING in a list until someone tries to act on it —
 * at which point the API retires it and refuses. Checking the deadline here keeps the buttons
 * honest instead of offering an action that is guaranteed to fail.
 */
export function approvalIsActionable(approval: {
	status?: ApprovalStatus | null;
	approvalWindowExpiresAt?: unknown;
}): boolean {
	if (approval.status !== ApprovalStatus.PENDING) return false;
	const expiresAt = approval.approvalWindowExpiresAt;
	if (typeof expiresAt !== "string") return true;
	return new Date(expiresAt).getTime() > Date.now();
}

// Key order is the order the approvals Risk filter renders, so these run most severe first rather
// than alphabetically.
export const ApprovalRiskLevel = {
	CRITICAL: "CRITICAL",
	HIGH: "HIGH",
	MEDIUM: "MEDIUM",
	LOW: "LOW",
} as const;

export type ApprovalRiskLevel =
	(typeof ApprovalRiskLevel)[keyof typeof ApprovalRiskLevel];

export type WorkspaceMember = NonNullable<
	NonNullable<GetWorkspaceMembersQuery["workspaceMembers"]>["nodes"]
>[number];

// Derived from the plural GetUsers query — the shape useOrgUsers actually returns
// (includes lastActivityAt), not the singular GetUser.
export type OrgUser = NonNullable<
	NonNullable<GetUsersQuery["users"]>["nodes"]
>[number];

export type ArtemisJob = NonNullable<NonNullable<GetJobQuery["job"]>>;

export type ArtemisJobRun = NonNullable<NonNullable<GetJobRunQuery["jobRun"]>>;

export const JobStatus = {
	ACTIVE: "ACTIVE",
	PAUSED: "PAUSED",
} as const;
export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];

export const JobRunTriggerType = {
	SCHEDULED: "SCHEDULED",
	MANUAL: "MANUAL",
} as const;
export type JobRunTriggerType =
	(typeof JobRunTriggerType)[keyof typeof JobRunTriggerType];

// Organization apps (ADR-061): the org-level catalog and per-workspace enablement.
export type OrganizationApp = NonNullable<
	GetOrganizationAppsQuery["organizationApps"]
>[number];
export type WorkspaceApp = NonNullable<
	GetWorkspaceAppsQuery["workspaceApps"]
>[number];

export const AppKind = {
	GIT_HUB: "GIT_HUB",
	PORT: "PORT",
} as const;
export type AppKind = (typeof AppKind)[keyof typeof AppKind];

export const AppStatus = {
	ENABLED: "ENABLED",
	DISABLED: "DISABLED",
} as const;
export type AppStatus = (typeof AppStatus)[keyof typeof AppStatus];

export const CallerKind = {
	USER: "USER",
	AGENT: "AGENT",
	APP: "APP",
} as const;
export type CallerKind = (typeof CallerKind)[keyof typeof CallerKind];

// Git provider connections & project repositories (REQ-090).
export type GitProviderConnection = NonNullable<
	GetGitProviderConnectionQuery["gitProviderConnection"]
>;
export type GitProviderRepository = NonNullable<
	GetGitProviderConnectionRepositoriesQuery["gitProviderConnectionRepositories"]
>["repositories"][number];
export type GitProviderCommit = NonNullable<
	GetGitProviderRepositoryCommitsQuery["gitProviderRepositoryCommits"]
>["commits"][number];
export type GitProviderBranch = NonNullable<
	GetGitProviderRepositoryBranchesQuery["gitProviderRepositoryBranches"]
>["branches"][number];
export type ProjectRepository = NonNullable<
	GetProjectRepositoriesQuery["projectRepositories"]
>[number];

// ── Webhooks (ARCH-035) ──────────────────────────────────────────────────────

export type ArtemisWebhook = NonNullable<GetWebhookQuery["webhook"]>;
export type ArtemisWebhookListItem = NonNullable<
	NonNullable<GetWebhooksQuery["webhooks"]>["nodes"]
>[number];
export type ArtemisWebhookDelivery = NonNullable<
	NonNullable<GetWebhookDeliveriesQuery["webhookDeliveries"]>["nodes"]
>[number];

export const WebhookAction = {
	CREATE_TASK: "CREATE_TASK",
	DISCOVER_DEPLOYMENT: "DISCOVER_DEPLOYMENT",
} as const;
export type WebhookAction = (typeof WebhookAction)[keyof typeof WebhookAction];

export const WebhookStatus = {
	ENABLED: "ENABLED",
	DISABLED: "DISABLED",
} as const;
export type WebhookStatus = (typeof WebhookStatus)[keyof typeof WebhookStatus];

export const WebhookDeliveryStatus = {
	SUCCEEDED: "SUCCEEDED",
	COALESCED: "COALESCED",
	FAILED: "FAILED",
} as const;
export type WebhookDeliveryStatus =
	(typeof WebhookDeliveryStatus)[keyof typeof WebhookDeliveryStatus];

// Signals: health sources outside Artemis (CloudWatch alarms first) tied to a project or one of
// its deployments. Definitions and live statuses come from separate queries so the rows render
// before the source answers.
export type Signal = GetSignalsQuery["signals"][number];
export type SignalStatus = GetSignalStatusesQuery["signals"][number]["status"];
export type SignalSourceInfo = GetSignalSourcesQuery["signalSources"][number];
export type SignalCandidate =
	GetSignalCandidatesQuery["signalCandidates"][number];

export type SignalState = (typeof SignalState)[keyof typeof SignalState];
export const SignalState = {
	OK: "OK",
	FIRING: "FIRING",
	WARNING: "WARNING",
	NO_DATA: "NO_DATA",
	UNKNOWN: "UNKNOWN",
} as const;

export type SignalKind = (typeof SignalKind)[keyof typeof SignalKind];
export const SignalKind = {
	ALARM: "ALARM",
	METRIC: "METRIC",
} as const;

export type ThresholdDirection =
	(typeof ThresholdDirection)[keyof typeof ThresholdDirection];
export const ThresholdDirection = {
	ABOVE: "ABOVE",
	BELOW: "BELOW",
} as const;

export type SignalConfigField = SignalSourceInfo["configFields"][number];

/**
 * Whether a signal counts towards the project's health: every alarm, and a metric only once it has
 * thresholds — without them a metric is informational (a number, not a verdict).
 */
export const isHealthSignal = (signal: Signal) =>
	signal.kind === SignalKind.ALARM ||
	signal.thresholds?.warning != null ||
	signal.thresholds?.critical != null;

export type SignalSource = (typeof SignalSource)[keyof typeof SignalSource];
export const SignalSource = {
	CLOUD_WATCH: "CLOUD_WATCH",
	CLOUD_WATCH_METRIC: "CLOUD_WATCH_METRIC",
	COGNITO_USERS: "COGNITO_USERS",
	AWS_COST: "AWS_COST",
} as const;

export type CatalogGraph = GetProjectCatalogGraphQuery["projectCatalogGraph"];
export type CatalogEntity = CatalogGraph["entities"][number];
export type CatalogRelation = CatalogGraph["relations"][number];
export type CatalogRepositoryStatus = CatalogGraph["repositories"][number];

export const CatalogRelationType = {
	CONSUMES_API: "CONSUMES_API",
	DEPENDS_ON: "DEPENDS_ON",
	PART_OF: "PART_OF",
	PROVIDES_API: "PROVIDES_API",
} as const;
export type CatalogRelationType =
	(typeof CatalogRelationType)[keyof typeof CatalogRelationType];

export const CatalogFileStatus = {
	FOUND: "FOUND",
	INVALID: "INVALID",
	MISSING: "MISSING",
	UNAVAILABLE: "UNAVAILABLE",
} as const;
export type CatalogFileStatus =
	(typeof CatalogFileStatus)[keyof typeof CatalogFileStatus];
